import React, { useRef, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';

interface SignaturePadProps {
  onSignatureChange: (signature: string | null) => void;
  disabled?: boolean;
  required?: boolean;
  width?: number;
  height?: number;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CanvasContainer = styled.div<{ disabled?: boolean; hasError?: boolean }>`
  border: 2px solid ${props => props.hasError ? 'var(--error-color)' : (props.disabled ? 'var(--border-light)' : 'var(--border-color)')};
  border-radius: var(--border-radius-md);
  background: ${props => props.disabled ? 'var(--background-light)' : 'white'};
  position: relative;
  cursor: ${props => props.disabled ? 'not-allowed' : 'crosshair'};
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

const Canvas = styled.canvas`
  display: block;
  width: 100%;
  height: 100%;
`;

const PlaceholderText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--text-secondary);
  font-size: 14px;
  pointer-events: none;
  text-align: center;
  line-height: 1.4;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 6px 12px;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover:not(:disabled) {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover:not(:disabled) { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover:not(:disabled) { background: var(--hover-background); }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--error-color);
`;

const SignaturePad: React.FC<SignaturePadProps> = ({
  onSignatureChange,
  disabled = false,
  required = false,
  width = 400,
  height = 200
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [lastPoint, setLastPoint] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = width;
    canvas.height = height;

    // Configure drawing style
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }, [width, height]);

  const getPointFromEvent = (event: MouseEvent | TouchEvent): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX: number, clientY: number;

    if (event instanceof MouseEvent) {
      clientX = event.clientX;
      clientY = event.clientY;
    } else {
      const touch = event.touches[0] || event.changedTouches[0];
      clientX = touch.clientX;
      clientY = touch.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (event: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;

    event.preventDefault();
    setIsDrawing(true);
    setHasError(false);

    const point = getPointFromEvent(event.nativeEvent);
    setLastPoint(point);

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    }
  };

  const draw = (event: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || disabled || !lastPoint) return;

    event.preventDefault();
    const point = getPointFromEvent(event.nativeEvent);

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx) {
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
    }

    setLastPoint(point);
  };

  const stopDrawing = () => {
    if (!isDrawing) return;

    setIsDrawing(false);
    setLastPoint(null);
    setHasSignature(true);

    // Convert canvas to base64 and notify parent
    // Use PNG format for cross-platform QuestPDF compatibility (Azure Linux support)
    const canvas = canvasRef.current;
    if (canvas) {
      const signature = canvas.toDataURL('image/png');
      onSignatureChange(signature);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx && canvas) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setHasSignature(false);
      setHasError(false);
      onSignatureChange(null);
    }
  };

  const validateSignature = useCallback((): boolean => {
    if (required && !hasSignature) {
      setHasError(true);
      return false;
    }
    setHasError(false);
    return true;
  }, [required, hasSignature]);

  // Expose validation to parent if needed
  useEffect(() => {
    if (required) {
      validateSignature();
    }
  }, [hasSignature, required, validateSignature]);

  return (
    <Container>
      <CanvasContainer disabled={disabled} hasError={hasError}>
        <Canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          style={{ touchAction: 'none' }}
        />
        {!hasSignature && !disabled && (
          <PlaceholderText>
            חתום כאן בעזרת העכבר או המגע
            <br />
            <small>או השתמש בעט דיגיטלי</small>
          </PlaceholderText>
        )}
        {disabled && (
          <PlaceholderText>
            חתימה לא זמינה
          </PlaceholderText>
        )}
      </CanvasContainer>

      <ButtonGroup>
        <Button 
          onClick={clearSignature} 
          disabled={disabled || !hasSignature}
          variant="danger"
        >
          נקה חתימה
        </Button>
      </ButtonGroup>

      {hasError && (
        <ErrorText>חתימה נדרשת</ErrorText>
      )}
    </Container>
  );
};

export default SignaturePad;